import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { isApiError, programApi } from "@bw/shared/api";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Link,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "../../../common/visuallyHidden";
import { PromotionSummaryCard } from "../../../common/components/PromotionSummaryCard";
import { Page } from "../../../common/components/Page";
import { DetailHeader, ShareButton } from "../../../common/components/DetailHeader";
import { DenseRow } from "../../../common/components/DenseRow";
import { DateBadge } from "../../../common/components/DateBadge";
import { ErrorCard, ListSkeleton } from "../../../common/components/states";
import {
  AuthContext,
  useProgramPreviewPage,
  useProgramPreviewQuery,
} from "@bw/shared/hooks";
import type {
  PublicProgramSessionPreviewPage,
  PublicProgramTrackOptionsPage,
} from "@bw/shared/types/generated";
import { useContext, useMemo } from "react";
import { useProgram } from "../hooks/useProgram";
import { publicPreviewScope } from "../hooks/previewKeys";
import { useLearningSpace } from "../hooks/useLearningSpace";
import { useProgramTracks } from "../hooks/useProgramTracks";
import {
  deriveSessionWindow,
  useProgramSessions,
} from "../hooks/useProgramSessions";
import {
  TrackAction,
  TrackRow,
  joinFacts,
  trackSummaryFacts,
} from "../components/TrackRow";
import { useCoachProfile } from "../../coaches/hooks/useCoachDetail";
import {
  programScheduleLabel,
  sessionDayLabel,
  sessionTimeLabel,
  titleCase,
} from "../components/classDisplay";
import {
  readFacilityDiscoveryReturn,
  withFacilityDiscoveryReturn,
} from "../../../common/facilityNavigation";
import { readSearchReturn } from "../../../common/searchNavigation";
import { usePublicFacility } from "../../booking/hooks/usePublicFacility";

/**
 * Surface 3 — program detail (/classes/:facilityId/:programId).
 *
 * Renders the program's REAL fields (name, description, type) plus a windowed
 * session preview and the buyable track list. Sessions are requested with a
 * bounded eight-week upcoming window; nominal course dates never truncate a
 * valid effective reschedule.
 *
 * The assigned instructor is a club-owned public profile. Current work-login
 * identity is intentionally never exposed to players.
 */
export function DetailPage() {
  const { facilityId, programId } = useParams<{
    facilityId: string;
    programId: string;
  }>();
  const location = useLocation();
  const facilitySearchReturn = readFacilityDiscoveryReturn(location.search);
  const searchReturn = readSearchReturn(location.search);
  const user = useContext(AuthContext)?.user ?? null;

  const programQ = useProgram(programId, facilityId, Boolean(user));
  const routeMatchesProgram = programQ.data?.facility_id === facilityId;
  const assignedCoachProfileId =
    routeMatchesProgram &&
    programQ.data &&
    "coach_profile_id" in programQ.data &&
    typeof programQ.data.coach_profile_id === "string"
      ? programQ.data.coach_profile_id
      : undefined;
  const coachQ = useCoachProfile(assignedCoachProfileId, facilityId);
  const learningSpaceQ = useLearningSpace(
    programId,
    Boolean(user && routeMatchesProgram),
  );
  const tracksQ = useProgramTracks(
    programId,
    Boolean(user && routeMatchesProgram),
  );
  const privateProgram =
    programQ.data &&
    "audience" in programQ.data &&
    programQ.data.audience === "private_coaching";
  const facilityQ = usePublicFacility(
    user && routeMatchesProgram && !privateProgram ? facilityId : undefined,
  );
  const sessionsQ = useProgramSessions(
    programId,
    Boolean(user && routeMatchesProgram && tracksQ.ready && !privateProgram),
  );
  const privateWindow = useMemo(deriveSessionWindow, [
    programId,
    programQ.identity,
  ]);
  const privateTrack = tracksQ.data?.items[0];
  const privateSessionsQ = useProgramPreviewQuery({
    scope: [
      programQ.identity,
      "classes",
      programId,
      facilityId,
      "authenticated",
      "private-exact-sessions",
      privateTrack?.track_id,
      privateWindow.window_start,
      privateWindow.window_end,
    ],
    load: () =>
      programApi.getSessions(programId as string, {
        ...privateWindow,
        ...(privateTrack ? { track_id: privateTrack.track_id } : {}),
      }),
    enabled: Boolean(
      user && routeMatchesProgram && privateProgram && tracksQ.ready,
    ),
  });
  const publicSessionsQ =
    useProgramPreviewPage<PublicProgramSessionPreviewPage>({
      scope: publicPreviewScope(
        programQ.identity,
        programId ?? "",
        facilityId ?? "",
        "sessions",
        programQ.window,
      ),
      enabled: !user && routeMatchesProgram && Boolean(programQ.window),
      initialPage: programQ.publicSessions,
      load: (cursor, signal) =>
        programApi.getPublicSessionPreview(
          programId as string,
          facilityId as string,
          { ...programQ.window!, limit: 50, ...(cursor ? { cursor } : {}) },
          signal,
        ),
    });
  const publicTracksQ = useProgramPreviewPage<PublicProgramTrackOptionsPage>({
    scope: publicPreviewScope(
      programQ.identity,
      programId ?? "",
      facilityId ?? "",
      "options",
    ),
    enabled: !user && routeMatchesProgram,
    initialPage: programQ.publicOptions,
    load: (cursor, signal) =>
      programApi.getPublicTrackOptions(
        programId as string,
        facilityId as string,
        { limit: 25, ...(cursor ? { cursor } : {}) },
        signal,
      ),
  });

  const back =
    searchReturn || facilityId
      ? {
          to:
            searchReturn ??
            withFacilityDiscoveryReturn(
              `/facility/${encodeURIComponent(facilityId as string)}`,
              facilitySearchReturn,
            ),
          label: searchReturn ? "Back to search" : "Back to facility",
        }
      : null;

  if (programQ.isLoading) {
    return (
      <Page narrow>
        <Stack spacing={1.5}>
          <DetailHeader back={back} title="Class" />
          <ListSkeleton label="Loading class…" rows={3} leading="square" />
        </Stack>
      </Page>
    );
  }

  if (programQ.isError || !programQ.data) {
    return (
      <Page narrow>
        <Stack spacing={1.5}>
          <DetailHeader back={back} title="Class" />
          <ErrorCard
            title="Couldn't load this class. Please try again."
            onRetry={() => void programQ.refetch()}
          />
        </Stack>
      </Page>
    );
  }

  if (!routeMatchesProgram) {
    return (
      <Page narrow>
        <Stack spacing={1.5}>
          <DetailHeader back={back} title="Class" />
          <ErrorCard
            title="This class is not available at this facility."
            body="It may have moved to another venue."
          />
        </Stack>
      </Page>
    );
  }

  const program = programQ.data;
  const publicPromotion =
    "promotion_summary" in program ? program.promotion_summary : null;
  const publicTracks = publicTracksQ.data?.items ?? [];
  const publicSessions = publicSessionsQ.data?.items ?? [];
  const tracks = user ? (tracksQ.data?.items ?? []) : publicTracks;
  const optionPager = user ? tracksQ : publicTracksQ;
  const currentSessionPage = user ? sessionsQ.data : publicSessionsQ.data;
  const sessionPager = user ? sessionsQ : publicSessionsQ;
  const sessions = user
    ? privateProgram
      ? privateSessionsQ.isSuccess && privateSessionsQ.fetchStatus === "idle"
        ? (privateSessionsQ.data ?? [])
        : []
      : (sessionsQ.data?.items.map((item) => ({
          ...item.session,
          track_name: item.track_name,
        })) ?? [])
    : publicSessions;
  const space = learningSpaceQ.data;
  const unavailableSpace =
    isApiError(learningSpaceQ.error) &&
    [401, 403, 404].includes(learningSpaceQ.error.status);
  const deadline = space ? new Date(space.archive_at) : null;
  const deadlineLabel =
    deadline && Number.isFinite(deadline.getTime())
      ? deadline.toLocaleString("en-US", {
          timeZone: "UTC",
          timeZoneName: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : null;


  const typeLabel = titleCase(program.program_type);
  const scheduleFact = programScheduleLabel(program);
  // One option (and no more pages): the chip names it, the facts line reads
  // "level · N sessions · spots", and its action sits in the enroll bar.
  const singleTrack =
    !optionPager.isLoading &&
    !optionPager.isError &&
    tracks.length === 1 &&
    !optionPager.canNext &&
    !optionPager.canPrevious
      ? tracks[0]
      : undefined;
  const sessionsComplete =
    sessions.length > 0 && !sessionPager.canNext && !sessionPager.canPrevious;
  const multiTrackSchedule = !singleTrack && tracks.length !== 1;
  const trackPromotion = (trackId: string) =>
    (facilityQ.data?.promotion_summaries ?? []).find(
      (item) =>
        item.service === "classes" &&
        (item.offering_id == null || item.offering_id === trackId),
    ) ??
    (publicPromotion?.offering_id == null ||
    publicPromotion?.offering_id === trackId
      ? (publicPromotion ?? undefined)
      : undefined);

  return (
    <Page narrow>
      <Stack spacing={2}>
        <DetailHeader
          back={back}
          title={program.name}
          subtitle={facilityQ.data?.name}
          actions={<ShareButton title={program.name} />}
        />

        {/* Option (or type) chip + the class's facts on one line. */}
        <Stack
          direction="row"
          spacing={1}
          data-testid={singleTrack ? "track-row" : undefined}
          // With one option, this line IS the options (its action is the bar).
          component={singleTrack ? "section" : "div"}
          aria-label={singleTrack ? "Options" : undefined}
          sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}
        >
          {singleTrack ? (
            <Chip label={singleTrack.name} color="primary" size="small" sx={{ fontWeight: 700 }} />
          ) : typeLabel ? (
            <Chip label={typeLabel} color="primary" size="small" sx={{ fontWeight: 700 }} />
          ) : null}
          <Typography variant="body2" color="text.secondary">
            {singleTrack
              ? joinFacts(
                  trackSummaryFacts(
                    singleTrack,
                    sessionsComplete ? sessions.length : null,
                  ),
                )
              : scheduleFact}
          </Typography>
        </Stack>

        {coachQ.data ? (
          <List disablePadding>
            <DenseRow
              to={withFacilityDiscoveryReturn(
                `/coaches/${coachQ.data.coach_profile_id}?facility_id=${encodeURIComponent(facilityId as string)}`,
                facilitySearchReturn,
              )}
              leading={
                <Avatar
                  src={coachQ.data.photo_url ?? undefined}
                  alt=""
                  sx={{ height: 44, width: 44, bgcolor: "action.selected", color: "text.primary", fontWeight: 700 }}
                >
                  {coachQ.data.display_name.slice(0, 1).toUpperCase()}
                </Avatar>
              }
              title={coachQ.data.display_name}
              subtitle="Coach"
            />
          </List>
        ) : null}

        {!user ? (
          <Button
            component={RouterLink}
            variant="contained"
            sx={{ alignSelf: "flex-start", borderRadius: 999 }}
            to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`}
          >
            Sign in to enroll
          </Button>
        ) : null}

        {space ? (
          <Stack
            component="section"
            spacing={1}
            aria-label="Class Learning Space"
            sx={{ alignItems: "flex-start" }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                component={RouterLink}
                size="small"
                variant="outlined"
                sx={{ borderRadius: 999 }}
                to={`/community/communities/${space.community_id}`}
              >
                Open Learning Space
              </Button>
              <Button component={RouterLink} size="small" to="/me/classes">
                My enrollments
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {space.status === "read_only"
                ? "Learner posting is now read-only. Class resources and conversation history remain available."
                : "Use the class Learning Space for resources and support."}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {deadlineLabel
                ? `Learner posting deadline: ${deadlineLabel}.`
                : "The posting deadline is unavailable right now."}
            </Typography>
          </Stack>
        ) : learningSpaceQ.isError && !unavailableSpace ? (
          <ErrorCard
            title="Couldn't load your Learning Space access. Please try again."
            body={null}
            retryLabel="Retry Learning Space"
            retrying={learningSpaceQ.isFetching}
            onRetry={() => void learningSpaceQ.refetch()}
          />
        ) : null}

        {/* Schedule — backend-expanded occurrences as date rows, rendered verbatim. */}
        <Stack component="section" spacing={0.5} aria-label="Schedule">
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", px: { xs: 0.5, sm: 0 } }}>
            <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700 }}>
              Schedule
            </Typography>
            {user ? (
              <Link component={RouterLink} to="/me/calendar" underline="none" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                Calendar
              </Link>
            ) : null}
          </Stack>
          {/* Which option an empty page belongs to (rows name their own). */}
          {!privateProgram && sessions.length === 0 && currentSessionPage?.examined_track_name ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 0 } }}>
              {currentSessionPage.examined_track_name}
            </Typography>
          ) : null}
          {!user && publicSessionsQ.isLoading ? (
            <ListSkeleton label="Loading schedule…" leading="square" />
          ) : !user && publicSessionsQ.isError ? (
            <Unavailable
              message="Schedule unavailable right now."
              retryLabel="Retry schedule"
              onRetry={privateProgram ? undefined : sessionPager.restart}
            />
          ) : user &&
            (tracksQ.isLoading ||
              (privateProgram ? privateSessionsQ.isLoading : sessionsQ.isLoading)) ? (
            <ListSkeleton label="Loading schedule…" leading="square" />
          ) : user &&
            (tracksQ.isError ||
              (privateProgram ? privateSessionsQ.isError : sessionsQ.isError)) ? (
            <Unavailable
              message="Schedule unavailable right now."
              retryLabel="Retry schedule"
              onRetry={privateProgram ? undefined : sessionPager.restart}
            />
          ) : sessions.length === 0 && currentSessionPage?.next_cursor ? (
            <Typography color="text.secondary" variant="body2">
              More options remain
            </Typography>
          ) : sessions.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No upcoming sessions in this window.
            </Typography>
          ) : (
            <List disablePadding>
              {sessions.map((s) => (
                <ListItem
                  key={`${s.track_id ?? "program"}-${s.occurrence_start}`}
                  data-testid="session-row"
                  disablePadding
                  sx={{ gap: 1.5, px: { xs: 0.5, sm: 1 }, py: 0.75 }}
                >
                  <DateBadge tile at={s.start_time} timeZone={s.timezone} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* The tile shows the date; the option and day stay readable. */}
                    <Box component="span" sx={visuallyHidden}>
                      {"track_name" in s && s.track_name ? `${s.track_name} · ` : ""}
                      {sessionDayLabel(s)}
                    </Box>
                    <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
                      {sessionTimeLabel(s) ?? "Time to be confirmed"}
                    </Typography>
                    {multiTrackSchedule && "track_name" in s && s.track_name ? (
                      <Typography aria-hidden noWrap variant="body2" color="text.secondary">
                        {s.track_name}
                      </Typography>
                    ) : null}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
          {!privateProgram && (sessionPager.canPrevious || sessionPager.canNext) ? (
            <Stack direction="row" spacing={1}>
              {sessionPager.canPrevious ? (
                <Button size="small" onClick={sessionPager.previous} disabled={!sessionPager.ready}>
                  Previous
                </Button>
              ) : null}
              {sessionPager.canNext ? (
                <Button size="small" onClick={sessionPager.next} disabled={!sessionPager.ready}>
                  {currentSessionPage?.examined_track_complete ? "Next option" : "Next sessions"}
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>

        {program.description ? (
          <Stack component="section" spacing={0.5} aria-label="About">
            <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700, px: { xs: 0.5, sm: 0 } }}>
              About
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 0.5, sm: 0 }, whiteSpace: "pre-line" }}>
              {program.description}
            </Typography>
          </Stack>
        ) : null}

        {/* Options — each a buyable row with its Enroll / Waitlist action. */}
        <Stack component="section" spacing={0.5} aria-label={singleTrack ? undefined : "Options"}>
          {singleTrack ? null : (
            <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700, px: { xs: 0.5, sm: 0 } }}>
              Options
            </Typography>
          )}
          {optionPager.isLoading ? (
            <ListSkeleton label="Loading options…" rows={1} leading="none" />
          ) : optionPager.isError ? (
            <Unavailable
              message="Enrollment options unavailable right now."
              retryLabel="Retry options"
              onRetry={optionPager.restart}
            />
          ) : singleTrack ? (
            singleTrack.status === "active" ? (
              <Box
                data-testid="enroll-bar"
                sx={{
                  position: { xs: "static", md: "sticky" },
                  bottom: { md: 0 },
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: "background.default",
                  px: { xs: 0.5, sm: 1 },
                  py: 1.5,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {scheduleFact ? (
                    <Typography variant="body2" color="text.secondary">
                      {scheduleFact}
                    </Typography>
                  ) : null}
                  {trackPromotion(singleTrack.track_id) ? (
                    <Box sx={{ mt: 0.5 }}>
                      <PromotionSummaryCard promotion={trackPromotion(singleTrack.track_id)!} compact />
                    </Box>
                  ) : null}
                </Box>
                <TrackAction
                  facilityId={facilityId as string}
                  programId={program.program_id}
                  track={singleTrack}
                  facilitySearchReturn={facilitySearchReturn}
                />
              </Box>
            ) : null
          ) : tracks.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No enrollment options yet.
            </Typography>
          ) : (
            <List disablePadding>
              {tracks.map((track) => (
                <TrackRow
                  key={track.track_id}
                  facilityId={facilityId as string}
                  programId={program.program_id}
                  track={track}
                  promotion={trackPromotion(track.track_id)}
                  facilitySearchReturn={facilitySearchReturn}
                />
              ))}
            </List>
          )}
          {optionPager.canPrevious || optionPager.canNext ? (
            <Stack direction="row" spacing={1}>
              {optionPager.canPrevious ? (
                <Button size="small" onClick={optionPager.previous}>
                  Previous options
                </Button>
              ) : null}
              {optionPager.canNext ? (
                <Button size="small" onClick={optionPager.next}>
                  Next options
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>

      </Stack>
    </Page>
  );
}

/** A section that couldn't load: one quiet line and a way to start it over. */
function Unavailable({
  message,
  retryLabel,
  onRetry,
}: {
  message: string;
  retryLabel: string;
  onRetry?: () => void;
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", px: { xs: 0.5, sm: 0 } }}>
      <Typography color="text.secondary" role="status" variant="body2" sx={{ flex: 1 }}>
        {message}
      </Typography>
      {onRetry ? (
        <Button size="small" variant="outlined" onClick={onRetry} sx={{ borderRadius: 999, flexShrink: 0 }}>
          {retryLabel}
        </Button>
      ) : null}
    </Stack>
  );
}
